import { Container, Sprite, Texture, Text, TextStyle } from 'pixi.js';
import { Assets } from 'pixi.js';

export interface ImageButtonStyle {
  fontSize?: number;
  fontColor?: number;
  scale?: number;
  holdToRepeat?: boolean;
}

export class ImageButton extends Container {
  private sprite: Sprite;
  private buttonText?: Text;
  private isEnabled: boolean = true;
  private isHovered: boolean = false;
  private callback?: () => void;
  private baseScale: number;
  private holdTimer?: ReturnType<typeof setTimeout>;
  private holdDelay: number = 300;
  private holdInterval: number = 100;
  private holdToRepeat: boolean;
  
  constructor(
    texture: Texture,
    text: string = '',
    style: ImageButtonStyle = {}
  ) {
    super();
    
    this.holdToRepeat = style.holdToRepeat ?? false;
    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);
    
    // Use scale instead of fixed dimensions to preserve aspect ratio
    this.baseScale = style.scale ?? 1;
    this.sprite.scale.set(this.baseScale);
    
    this.addChild(this.sprite);
    
    // Add text overlay if provided
    if (text) {
      const textStyle = new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: style.fontSize ?? 24,
        fontWeight: 'bold',
        fill: style.fontColor ?? 0xFFFFFF,
        stroke: { color: 0x000000, width: 2 },
        dropShadow: {
          color: 0x000000,
          blur: 4,
          distance: 2
        }
      });
      
      this.buttonText = new Text({ text, style: textStyle });
      this.buttonText.anchor.set(0.5);
      this.addChild(this.buttonText);
    }
    
    this.setupInteractivity();
  }
  
  public get width(): number {
    return this.sprite.width * this.baseScale;
  }
  
  public get height(): number {
    return this.sprite.height * this.baseScale;
  }
  
  private setupInteractivity(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';
    
    this.on('pointerover', () => {
      if (this.isEnabled) {
        this.isHovered = true;
        this.sprite.scale.set(this.baseScale * 1.05);
      }
    });
    
    this.on('pointerout', () => {
      this.isHovered = false;
      this.sprite.scale.set(this.baseScale);
      if (this.holdTimer) {
        clearTimeout(this.holdTimer);
        clearInterval(this.holdTimer);
        this.holdTimer = undefined;
      }
    });
    
    this.on('pointerdown', () => {
      if (this.isEnabled) {
        this.sprite.scale.set(this.baseScale * 0.95);
        
        // Fire once immediately
        if (this.callback) {
          this.callback();
        }
        
        // Only setup hold-to-repeat if enabled
        if (this.holdToRepeat) {
          // Clear any existing timer
          if (this.holdTimer) {
            clearTimeout(this.holdTimer);
            clearInterval(this.holdTimer);
          }
          
          // Start hold-to-repeat
          this.holdTimer = setTimeout(() => {
            if (this.callback) {
              // Start rapid firing after initial delay
              this.holdTimer = setInterval(() => {
                if (this.callback) {
                  this.callback();
                }
              }, this.holdInterval);
            }
          }, this.holdDelay);
        }
      }
    });
    
    this.on('pointerup', () => {
      if (this.isEnabled) {
        this.sprite.scale.set(this.isHovered ? this.baseScale * 1.05 : this.baseScale);
        if (this.holdTimer) {
          clearTimeout(this.holdTimer);
          clearInterval(this.holdTimer);
          this.holdTimer = undefined;
        }
      }
    });
  }
  
  public setCallback(callback: () => void): void {
    this.callback = callback;
  }
  
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.alpha = enabled ? 1 : 0.5;
  }
  
  public setText(text: string): void {
    if (this.buttonText) {
      this.buttonText.text = text;
    }
  }
  
  public update(delta: number): void {
    // No continuous update needed
  }
  
  public destroy(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      clearInterval(this.holdTimer);
      this.holdTimer = undefined;
    }
    super.destroy();
  }
}

// Helper function to create image button from texture path
export async function createImageButton(
  texturePath: string,
  text: string = '',
  style: ImageButtonStyle = {}
): Promise<ImageButton> {
  const texture = await Assets.load(texturePath);
  return new ImageButton(texture, text, style);
}